/**
 * Tarot Mapper
 *
 * Maps personality types from backend to tarot card visuals and data.
 * Backend provides: personality_type (string) and summary (string).
 * This mapper provides card aesthetics: number, color, image, tagline.
 */

/**
 * Tarot Card Visual Definitions
 *
 * Maps personality_type to card aesthetics.
 * Summary comes directly from backend and is displayed on card back.
 */
// Roman numeral order:
// I - The Fucking Delight
// II - Emotional Dump Truck
// III - Chaos Goblin
// IV - The Velvet Teddy
// V - Sassy Cynic
// VI - The Worry Witch
// VII - The Group Chat CEO
// VIII - Sweet with a hint of Psycho
export const PERSONALITY_CARD_STYLES = {
  'The Fucking Delight': {
    cardNumber: 'I',
    backgroundColor: '#9D7AB8', // Purple
    cardImage: '/assets/images/tarot-cards/Fucking-delight.png',
    frontContent: {
      subtitle: 'The Radiant Joy',
      tagline: '*spreading sunshine wherever you go',
    },
  },

  'Emotional Dump Truck': {
    cardNumber: 'II',
    backgroundColor: '#7DB8E6', // Blue
    cardImage: '/assets/images/tarot-cards/Emotional-dump-truck.png',
    frontContent: {
      subtitle: 'The Authentic Feeler',
      tagline: '*all the feelings, all the time',
    },
  },

  'Chaos Goblin': {
    cardNumber: 'III',
    backgroundColor: '#E6B87D', // Gold/yellow
    cardImage: '/assets/images/tarot-cards/Chaos-goblin.png',
    frontContent: {
      subtitle: 'The Creative Disruptor',
      tagline: '*embracing the beautiful mess',
    },
  },

  'The Velvet Teddy': {
    cardNumber: 'IV',
    backgroundColor: '#D4A574', // Warm brown/teddy color
    cardImage: '/assets/images/tarot-cards/Velvet-teddy.png',
    frontContent: {
      subtitle: 'The Warm Companion',
      tagline: '*radiating Golden Retriever energy',
    },
  },

  // Alias for backend compatibility
  'Velvet Teddy': {
    cardNumber: 'IV',
    backgroundColor: '#D4A574', // Warm brown/teddy color
    cardImage: '/assets/images/tarot-cards/Velvet-teddy.png',
    frontContent: {
      subtitle: 'The Warm Companion',
      tagline: '*radiating Golden Retriever energy',
    },
  },

  'Sassy Cynic': {
    cardNumber: 'V',
    backgroundColor: '#6BA876', // Green
    cardImage: '/assets/images/tarot-cards/Sassy-cynic.png',
    frontContent: {
      subtitle: 'The Witty Realist',
      tagline: '*keeping it real with a side of snark',
    },
  },

  'The Worry Witch': {
    cardNumber: 'VI',
    backgroundColor: '#4A4A4A', // Dark grey
    cardImage: '/assets/images/tarot-cards/The-worry-witch.png',
    frontContent: {
      subtitle: 'The Cautious Planner',
      tagline: '*brewing anxiety potions since birth',
    },
  },

  'The Group Chat CEO': {
    cardNumber: 'VII',
    backgroundColor: '#5A7A9B', // Deep blue
    cardImage: '/assets/images/tarot-cards/Group-chat-ceo.png',
    frontContent: {
      subtitle: 'The Social Connector',
      tagline: '*running the show, one message at a time',
    },
  },

  'Sweet with a Hint of Psycho': {
    cardNumber: 'VIII',
    backgroundColor: '#E67DB8', // Pink
    cardImage: '/assets/images/tarot-cards/Sweet-with-a-hint-of-psycho.png',
    frontContent: {
      subtitle: 'The Passionate Protector',
      tagline: '*sugar, spice, and everything intense',
    },
  },
};

/**
 * Map backend personality type to complete tarot card data
 *
 * @param {string} personality_type - Personality type from backend
 * @param {string} summary - Full summary text from backend
 * @param {string} summary_short - Short summary for mobile share images (optional)
 * @returns {Object|null} Complete tarot card data or null if not found
 */
export const mapPersonalityToCard = (personality_type, summary, summary_short = null) => {
  // Get the card style for this personality type
  const cardStyle = PERSONALITY_CARD_STYLES[personality_type];

  if (!cardStyle) {
    console.warn(`⚠️ Unknown personality type: "${personality_type}"`);
    return null;
  }

  // Combine card style with backend summary
  return {
    cardNumber: cardStyle.cardNumber,
    cardTitle: personality_type, // Use exact personality_type from backend
    backgroundColor: cardStyle.backgroundColor,
    cardImage: cardStyle.cardImage, // null for placeholders
    frontContent: cardStyle.frontContent,
    backContent: {
      summary: summary, // Full summary from backend
      summary_short: summary_short, // Short summary for mobile share (optional)
    },
  };
};

/**
 * Get tarot card data for a specific personality type
 * (Legacy function for compatibility)
 *
 * @param {string} personalityType - The personality type name
 * @returns {Object|null} Tarot card style data or null if not found
 */
export const getTarotCard = (personalityType) => {
  return PERSONALITY_CARD_STYLES[personalityType] || null;
};

/**
 * Get all available personality types
 *
 * @returns {Array<string>} List of all personality type names
 */
export const getAllPersonalityTypes = () => {
  return Object.keys(PERSONALITY_CARD_STYLES);
};

/**
 * Check if a personality type is supported
 *
 * @param {string} personalityType - Personality type to check
 * @returns {boolean} True if supported
 */
export const isPersonalityTypeSupported = (personalityType) => {
  return personalityType in PERSONALITY_CARD_STYLES;
};

/**
 * Validate tarot card data structure
 *
 * @param {Object} cardData - Tarot card data to validate
 * @returns {boolean} True if valid
 */
export const isValidTarotCard = (cardData) => {
  if (!cardData) return false;

  const requiredFields = ['cardNumber', 'cardTitle', 'backgroundColor'];
  const hasRequiredFields = requiredFields.every((field) => cardData[field]);

  const hasFrontContent = cardData.frontContent && typeof cardData.frontContent === 'object';
  const hasBackContent = cardData.backContent && typeof cardData.backContent === 'object';

  return hasRequiredFields && hasFrontContent && hasBackContent;
};

export default {
  PERSONALITY_CARD_STYLES,
  mapPersonalityToCard,
  getTarotCard,
  getAllPersonalityTypes,
  isPersonalityTypeSupported,
  isValidTarotCard,
};
